/**
 * PeriodSelector — the left pill in the chart's date-range row.
 *
 * Renders a pill button + dropdown of preset windows (Today / Yesterday /
 * 24 Hours / 7 Days / 30 Days / 90 Days / Custom Range) plus, at the bottom,
 * a "Compare" entry that activates period-over-period comparison without
 * changing the currently-picked window — it just adds `?comparison=previous_period`
 * to the URL. AnalyticsPage watches that param and renders ComparisonPicker
 * next to this selector ("[ Last 28 days ] vs [ Previous period ▾ ]").
 *
 * URL state owned here:
 *   ?period=<today|yesterday|24h|7d|30d|90d|custom>
 *   ?start_date / ?end_date   (only when period=custom)
 *   ?comparison=previous_period  (added by the Compare item — defaults from there)
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, GitCompare, Check } from 'lucide-react';
import DatePickerInput from './DatePickerInput';

const PERIOD_OPTIONS = [
	{ value: 'today', label: 'Today' },
	{ value: 'yesterday', label: 'Yesterday' },
	{ value: '24h', label: '24 Hours' },
	{ value: '7d', label: '7 Days' },
	{ value: '30d', label: '30 Days' },
	{ value: '90d', label: '90 Days' },
	{ value: 'custom', label: 'Custom Range' },
];

const PeriodSelector = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const currentPeriod = searchParams.get('period') || 'today';
	const comparisonActive = !!searchParams.get('comparison') && searchParams.get('comparison') !== 'none';
	const [showDropdown, setShowDropdown] = useState(false);
	const [showCustom, setShowCustom] = useState(false);
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');

	const getPeriodLabel = (value) => {
		// Show "Custom: 2024-01-01 - 2024-01-31" when the URL has start_date / end_date.
		if (value === 'custom') {
			const sd = searchParams.get('start_date');
			const ed = searchParams.get('end_date');
			if (sd && ed) return `${sd} → ${ed}`;
			return 'Custom Range';
		}
		const opt = PERIOD_OPTIONS.find(p => p.value === value);
		return opt ? opt.label : 'Today';
	};

	const handlePeriodChange = (value) => {
		if (value === 'custom') {
			setShowCustom(true);
			setShowDropdown(false);
			return;
		}
		const params = new URLSearchParams(searchParams);
		params.set('period', value);
		params.delete('start_date');
		params.delete('end_date');
		navigate(`?${params.toString()}`, { replace: true });
		setShowDropdown(false);
	};

	// "Compare" sits below "Custom Range" in the dropdown. It does NOT change
	// the period — it just toggles comparison on by setting ?comparison=previous_period.
	// If comparison is already on, clicking it is a no-op (matches Plausible).
	const handleEnableCompare = () => {
		const params = new URLSearchParams(searchParams);
		params.set('comparison', 'previous_period');
		// Drop any stale comparison_* modifiers from a previous session so we
		// start in a clean "Previous period + default day-of-week alignment" state.
		params.delete('comparison_match');
		params.delete('comparison_start');
		params.delete('comparison_end');
		navigate(`?${params.toString()}`, { replace: true });
		setShowDropdown(false);
	};

	const handleCustomApply = () => {
		if (!startDate || !endDate) return;
		const params = new URLSearchParams(searchParams);
		params.set('period', 'custom');
		params.set('start_date', startDate);
		params.set('end_date', endDate);
		navigate(`?${params.toString()}`, { replace: true });
		setShowCustom(false);
		setShowDropdown(false);
	};

	return (
		<div className="period-selector-wrap">
			<button
				className="period-pill"
				onClick={() => setShowDropdown(!showDropdown)}
				type="button"
			>
				<Calendar size={14} className="period-pill__icon" />
				<span className="period-pill__label">{getPeriodLabel(currentPeriod)}</span>
				<svg width="10" height="10" viewBox="0 0 12 12" className="period-pill__chev">
					<path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" />
				</svg>
			</button>

			{showDropdown && (
				<>
					<div className="period-dropdown__scrim" onClick={() => setShowDropdown(false)} />
					<div className="period-dropdown">
						{PERIOD_OPTIONS.map(opt => (
							<button
								key={opt.value}
								type="button"
								className={`period-dropdown__item ${currentPeriod === opt.value ? 'is-active' : ''}`}
								onClick={() => handlePeriodChange(opt.value)}
							>
								<span>{opt.label}</span>
								{currentPeriod === opt.value && <Check size={14} className="period-dropdown__check" />}
							</button>
						))}

						{/* Divider before the Compare item — visually separates "pick a window"
						    from the comparison toggle, matching the layout in image 1. */}
						<div className="period-dropdown__divider" />

						<button
							type="button"
							className={`period-dropdown__item period-dropdown__compare ${comparisonActive ? 'is-active' : ''}`}
							onClick={handleEnableCompare}
							title={comparisonActive ? 'Comparison already enabled' : 'Compare to previous period'}
						>
							<span className="period-dropdown__compare-label">
								<GitCompare size={14} />
								Compare
							</span>
							{comparisonActive && <Check size={14} className="period-dropdown__check" />}
						</button>
					</div>
				</>
			)}

			{showCustom && (
				<>
					<div className="period-custom__scrim" onClick={() => setShowCustom(false)} />
					<div className="period-custom__modal">
						<h3 className="period-custom__title">Custom Date Range</h3>
						<div className="period-custom__field">
							<label>Start Date</label>
							<DatePickerInput
								value={startDate}
								onChange={setStartDate}
								placeholder="Pick start date"
								max={endDate || undefined}
							/>
						</div>
						<div className="period-custom__field">
							<label>End Date</label>
							<DatePickerInput
								value={endDate}
								onChange={setEndDate}
								placeholder="Pick end date"
								min={startDate || undefined}
							/>
						</div>
						<div className="period-custom__actions">
							<button
								type="button"
								className="period-custom__btn period-custom__btn--ghost"
								onClick={() => setShowCustom(false)}
							>
								Cancel
							</button>
							<button
								type="button"
								className="period-custom__btn period-custom__btn--primary"
								disabled={!startDate || !endDate}
								onClick={handleCustomApply}
							>
								Apply
							</button>
						</div>
					</div>
				</>
			)}
		</div>
	);
};

export default PeriodSelector;
